import {useFormik} from 'formik';
import * as Yup from 'yup';
import {useState} from 'react';
import {AxiosError} from 'axios';
import {useFundraiser} from '../../../entities';
import {BenefactorTransferFormData, BenefactorTransferDTO} from '../../../entities';

interface UseBenefactorTransferFormProps {
  fundraiserId: string;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export const useBenefactorTransferForm = ({fundraiserId, setOpen, onSuccess}: UseBenefactorTransferFormProps) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formDataForConfirm, setFormDataForConfirm] = useState<BenefactorTransferDTO | null>(null);
  const {transferFromBenefactorMutation} = useFundraiser({id: fundraiserId});

  const validationSchema = Yup.object({
    benefactorId: Yup.string().required('Выберите донора'),
    amount: Yup.number()
      .typeError('Должно быть числом')
      .required('Сумма обязательна')
      .positive('Сумма должна быть положительной'),

    serviceFee: Yup.number()
      .typeError('Должно быть числом')
      .min(0, 'Комиссия не может быть отрицательной')
      .required('Комиссия обязательна'),
    bookkeepingNumber: Yup.string(),
    note: Yup.string(),
  });

  const onSubmit = async (values: BenefactorTransferFormData) => {
    const transferData: BenefactorTransferDTO = {
      benefactorId: values.benefactorId,
      amount: parseFloat(values.amount),
      serviceFee: parseFloat(values.serviceFee),
      bookkeepingNumber: values.bookkeepingNumber || null,
      note: values.note || null,
    };

    setFormDataForConfirm(transferData);
    setShowConfirmModal(true);
  };

  const handleConfirmTransfer = async () => {
    if (!formDataForConfirm) return;

    try {
      await transferFromBenefactorMutation.mutateAsync(formDataForConfirm);
      setShowConfirmModal(false);
      setOpen(false);
      formik.resetForm();

      onSuccess?.();
      setTimeout(() => {
        window.location.reload();
      }, 2500);
    } catch (error) {
      console.error('Ошибка при внесении средств:', error);
    }
  };

  const formik = useFormik<BenefactorTransferFormData>({
    initialValues: {
      benefactorId: '',
      amount: '',
      serviceFee: '',
      bookkeepingNumber: '',
      note: '',
    },
    validationSchema,
    onSubmit,
  });

  return {
    ...formik,
    setFieldTouched: formik.setFieldTouched,
    isLoading: transferFromBenefactorMutation.isPending,
    submitError: transferFromBenefactorMutation.error
      ? (transferFromBenefactorMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
    showConfirmModal,
    setShowConfirmModal,
    formDataForConfirm,
    handleConfirmTransfer,
    submitForm: () => formik.handleSubmit(),
  };
};
